<script setup lang="ts">
import { computed, useId } from 'vue';

/**
 * Barra horizontal de volume, uma por categoria do mixer de som: botão de mudo, nome, barra e porcentagem.
 * `modelValue` vai de 0 a 1. `commit` dispara quando o jogador solta a barra (a tela toca um som de exemplo).
 */
const props = withDefaults(defineProps<{ modelValue: number; label: string; hint?: string; muted?: boolean }>(), { hint: '', muted: false });
const emit = defineEmits<{ 'update:modelValue': [value: number]; 'update:muted': [value: boolean]; commit: [] }>();

const id = useId();
const percent = computed(() => Math.round(Math.min(1, Math.max(0, props.modelValue)) * 100));

function onInput(event: Event) {
  const value = Number((event.target as HTMLInputElement).value) / 100;
  emit('update:modelValue', value);
  // Mexer na barra de uma categoria silenciada liga o som dela de novo.
  if (props.muted && value > 0) emit('update:muted', false);
}
</script>

<template>
  <div class="ddt-volume" :class="{ 'ddt-volume--muted': muted || percent === 0 }" data-testid="volume-row">
    <button
      type="button"
      class="ddt-volume__mute"
      :aria-pressed="muted"
      :aria-label="muted ? `Ligar ${label}` : `Silenciar ${label}`"
      data-no-click-sound
      @click="emit('update:muted', !muted)"
    >
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path d="M4 9v6h4l5 4V5L8 9z" fill="currentColor" />
        <path v-if="muted || percent === 0" d="M16 9l5 6m0-6l-5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" />
        <path v-else d="M16 8.5a5 5 0 010 7M18.5 6a8.5 8.5 0 010 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none" />
      </svg>
    </button>
    <label class="ddt-volume__text" :for="id">
      <span class="ddt-volume__label">{{ label }}</span>
      <span v-if="hint" class="ddt-volume__hint">{{ hint }}</span>
    </label>
    <input
      :id="id"
      class="ddt-volume__range"
      type="range"
      min="0"
      max="100"
      step="1"
      :value="percent"
      :style="{ '--ddt-volume': `${percent}%` }"
      :aria-valuetext="muted ? 'silenciado' : `${percent}%`"
      @input="onInput"
      @change="emit('commit')"
    />
    <span class="ddt-volume__value" aria-hidden="true">{{ muted ? '—' : `${percent}%` }}</span>
  </div>
</template>
